import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, X } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { api, ApiError } from '@/lib/api';

import {
  openedLabelParts,
  type CloseEmptyTabsResult,
  type EmptyTabRow,
  type EmptyTabsResponse,
} from './liveTabs';

/**
 * "Close empty tabs" — the owner's one button for the dead tabs the old rules
 * left OPEN (owner, 2026-09-23: "when i type 'Yaran staff' there are two tabs
 * even tho in the outstanding bill there is only one").
 *
 * Settlements close a finished tab by themselves now; this is for the ones
 * already stuck — 388 in production that morning. It LISTS exactly which tabs
 * owe nothing and have nothing running, the owner reads it, confirms, and the
 * server closes exactly those, re-checking each one first: a tab somebody
 * rang a drink onto meanwhile is skipped and reported, never closed. No money
 * moves, and every close is written to the audit log with the owner's name.
 */
export function CloseEmptyTabsDialog({
  onClose,
  onClosedSome,
}: {
  onClose: () => void;
  /** After a close that changed something, so the caller can refresh its list. */
  onClosedSome?: () => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<EmptyTabRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CloseEmptyTabsResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<EmptyTabsResponse>('/tabs/empty');
        if (!cancelled) setRows(response.tabs);
      } catch (caught) {
        if (!cancelled) {
          setError(
            caught instanceof ApiError ? caught.message : t('openTabs.closeEmpty.loadFailed'),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  async function confirm(): Promise<void> {
    if (!rows || rows.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await api.post<CloseEmptyTabsResult>('/tabs/close-empty', {
        tabIds: rows.map((row) => row.tabId),
      });
      setResult(outcome);
      if (outcome.closed > 0) onClosedSome?.();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('openTabs.closeEmpty.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('openTabs.closeEmpty.title')}
      data-close-empty-tabs
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 px-4 pt-16 sm:pt-24"
      onClick={submitting ? undefined : onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-card border border-subtle bg-raised shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-subtle px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-content">
            <Archive className="h-4 w-4 text-content-secondary" aria-hidden />
            {t('openTabs.closeEmpty.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label={t('openTabs.closeEmpty.close')}
            className="rounded-full p-1.5 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <CloseEmptyTabsBody rows={rows} error={error} result={result} />

        <div className="flex flex-wrap justify-end gap-2 border-t border-subtle px-4 py-3">
          {result ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
            >
              {t('openTabs.closeEmpty.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                data-confirm-close-empty
                onClick={() => void confirm()}
                disabled={submitting || !rows || rows.length === 0}
                className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
              >
                {t('openTabs.closeEmpty.confirm', { count: rows?.length ?? 0 })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What the dialog says, given where it is — separate so every state renders
 * without a network.
 */
export function CloseEmptyTabsBody({
  rows,
  error,
  result,
  now = new Date(),
}: {
  rows: readonly EmptyTabRow[] | null;
  error: string | null;
  result: CloseEmptyTabsResult | null;
  now?: Date;
}) {
  const { t, i18n } = useTranslation();

  if (result) {
    const changed = result.skipped.filter((row) => row.reason === 'not_empty').length;
    return (
      <div className="space-y-2 p-4 text-sm text-content" data-close-empty-result>
        <p>{t('openTabs.closeEmpty.done', { count: result.closed })}</p>
        {changed > 0 ? (
          <p className="text-content-secondary">
            {t('openTabs.closeEmpty.skippedChanged', { count: changed })}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-danger">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      <p className="text-sm text-content-secondary">{t('openTabs.closeEmpty.intro')}</p>
      <p className="mt-1 text-xs text-content-muted">{t('openTabs.closeEmpty.safety')}</p>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {rows === null ? (
        error ? null : (
          <p className="mt-3 text-sm text-content-muted">{t('common.loading')}</p>
        )
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-content" data-close-empty-none>
          {t('openTabs.closeEmpty.none')}
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm font-medium text-content">
            {t('openTabs.closeEmpty.count', { count: rows.length })}
          </p>
          <ul className="mt-2 divide-y divide-subtle rounded-card border border-subtle">
            {rows.map((row) => {
              const opened = openedLabelParts(row.openedAt, now, i18n.language);
              return (
                <li
                  key={row.tabId}
                  data-empty-tab={row.tabId}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-3 py-2"
                >
                  <span className="min-w-0 truncate text-sm text-content">{row.name}</span>
                  <span className="tabular text-[11px] text-content-muted">
                    {[
                      opened.today
                        ? t('openTabs.card.openedToday', { time: opened.text })
                        : t('openTabs.card.openedOn', { date: opened.text }),
                      t('openTabs.closeEmpty.sittings', { count: row.sittingCount }),
                      t('openTabs.closeEmpty.paidBills', { count: row.paidBillCount }),
                    ].join(' · ')}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * The owner's button and its dialog, self-contained so a host screen mounts
 * it with one line and owns none of its state. Renders nothing for anyone
 * else — the routes behind it are owner-only too.
 */
export function CloseEmptyTabsButton({ onClosedSome }: { onClosedSome?: () => void }) {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const [open, setOpen] = useState(false);

  if (!employee?.isOwner) return null;

  return (
    <>
      <button
        type="button"
        data-close-empty-tabs-button
        onClick={() => setOpen(true)}
        className="inline-flex min-h-9 items-center gap-1.5 rounded-control border border-subtle bg-input px-3 text-sm text-content-secondary hover:bg-hover hover:text-content"
      >
        <Archive className="h-4 w-4" aria-hidden />
        {t('openTabs.closeEmpty.button')}
      </button>
      {open ? (
        <CloseEmptyTabsDialog onClose={() => setOpen(false)} onClosedSome={onClosedSome} />
      ) : null}
    </>
  );
}
